import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Mail,
  User,
  ChevronDown,
  ChevronRight,
  Building2,
  Stethoscope,
  ShieldCheck,
  ShieldOff,
  Hash,
  Layers,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface AdminUser {
  userId: string;
  role: string;
  approvalStatus: "pending" | "approved" | "rejected";
  displayName: string;
  email: string;
  jobRole: string | null;
  organisationName: string | null;
  teamCode: string | null;
  band: string | null;
  registrationBody: string | null;
  registrationNumber: string | null;
  registrationVerified: boolean | null;
  createdAt: string;
}

type StatusFilter = "all" | "pending" | "approved" | "rejected";
type ActionKind = "approve" | "reject";

interface PendingAction {
  userId: string;
  displayName: string;
  kind: ActionKind;
}

const ROLE_LABELS: Record<string, string> = {
  nurse: "Nurse",
  doctor: "Doctor",
  pharmacist: "Pharmacist",
  physio: "Physiotherapist",
  ot: "Occupational Therapist",
  patient: "Patient",
};

const ROLE_COLOURS: Record<string, string> = {
  nurse: "bg-teal-100 text-teal-800 border-teal-200",
  doctor: "bg-blue-100 text-blue-800 border-blue-200",
  pharmacist: "bg-violet-100 text-violet-800 border-violet-200",
  physio: "bg-orange-100 text-orange-800 border-orange-200",
  ot: "bg-pink-100 text-pink-800 border-pink-200",
  patient: "bg-rose-100 text-rose-800 border-rose-200",
};

const STATUS_COLOURS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const REGISTRATION_BODY_LABELS: Record<string, string> = {
  nmc: "NMC",
  gmc: "GMC",
  gphc: "GPhC",
  hcpc: "HCPC",
  other: "Other",
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? "Request failed");
  }
  return res.json() as Promise<unknown>;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={`text-xs font-medium gap-1 ${STATUS_COLOURS[status] ?? ""}`}>
      {status === "pending" && <Clock className="h-3 w-3" />}
      {status === "approved" && <CheckCircle2 className="h-3 w-3" />}
      {status === "rejected" && <XCircle className="h-3 w-3" />}
      {STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

function ProfileDetail({ label, value, icon: Icon }: { label: string; value: React.ReactNode; icon?: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">{label}</span>
      <span className="text-sm text-slate-700 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
        {value ?? <span className="text-slate-400 italic">Not provided</span>}
      </span>
    </div>
  );
}

function UserProfilePanel({ user }: { user: AdminUser }) {
  const hasAnyProfileData =
    user.jobRole || user.organisationName || user.teamCode || user.band || user.registrationBody || user.registrationNumber || user.registrationVerified != null;

  if (!hasAnyProfileData) {
    return (
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-400 italic">
        No onboarding profile submitted yet.
      </div>
    );
  }

  return (
    <div className="px-4 py-4 bg-slate-50 border-t border-slate-100">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4">
        <ProfileDetail label="Job role" value={user.jobRole} icon={Stethoscope} />
        <ProfileDetail label="Organisation" value={user.organisationName} icon={Building2} />
        <ProfileDetail
          label="Team code"
          value={user.teamCode ? <span className="font-mono">{user.teamCode}</span> : null}
          icon={Hash}
        />
        <ProfileDetail label="Band / grade" value={user.band} icon={Layers} />
        <ProfileDetail label="Reg. body" value={user.registrationBody ? (REGISTRATION_BODY_LABELS[user.registrationBody] ?? user.registrationBody) : null} icon={Hash} />
        <ProfileDetail label="Reg. number" value={user.registrationNumber} icon={Hash} />
        <ProfileDetail
          label="Reg. verified"
          value={
            user.registrationVerified === true ? (
              <span className="flex items-center gap-1 text-green-700">
                <ShieldCheck className="h-3.5 w-3.5" />
                Verified
              </span>
            ) : user.registrationVerified === false ? (
              <span className="flex items-center gap-1 text-amber-700">
                <ShieldOff className="h-3.5 w-3.5" />
                Not verified
              </span>
            ) : null
          }
        />
      </div>
    </div>
  );
}

function UserRow({
  user,
  onApprove,
  onReject,
  isActing,
}: {
  user: AdminUser;
  onApprove: (userId: string, displayName: string) => void;
  onReject: (userId: string, displayName: string) => void;
  isActing: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const formattedDate = new Date(user.createdAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <>
      <tr
        className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label={expanded ? "Collapse profile" : "Expand profile"}
              tabIndex={-1}
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 flex-shrink-0">
              <User className="h-4 w-4 text-slate-500" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user.displayName || <span className="text-slate-400 italic">No name</span>}
              </p>
              {user.email && (
                <a
                  href={`mailto:${user.email}`}
                  className="text-xs text-slate-400 hover:text-teal-700 flex items-center gap-1 truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  {user.email}
                </a>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <Badge
            variant="outline"
            className={`text-xs font-medium ${ROLE_COLOURS[user.role] ?? "bg-slate-100 text-slate-600 border-slate-200"}`}
          >
            {ROLE_LABELS[user.role] ?? user.role}
          </Badge>
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={user.approvalStatus} />
        </td>
        <td className="px-4 py-3 text-xs whitespace-nowrap">
          {user.teamCode ? (
            <span className="font-mono text-slate-700">{user.teamCode}</span>
          ) : (
            <span className="text-slate-300">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formattedDate}</td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            {user.approvalStatus !== "approved" && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-xs gap-1 text-green-700 border-green-200 hover:bg-green-50 hover:border-green-300"
                disabled={isActing}
                onClick={() => onApprove(user.userId, user.displayName)}
              >
                <CheckCircle2 className="h-3 w-3" />
                Approve
              </Button>
            )}
            {user.approvalStatus !== "rejected" && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                disabled={isActing}
                onClick={() => onReject(user.userId, user.displayName)}
              >
                <XCircle className="h-3 w-3" />
                Reject
              </Button>
            )}
            {user.approvalStatus === "approved" && (
              <span className="text-xs text-slate-400 italic">Access granted</span>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-slate-100 last:border-0">
          <td colSpan={5} className="p-0">
            <UserProfilePanel user={user} />
          </td>
        </tr>
      )}
    </>
  );
}

function ActionDialog({
  pending,
  isActing,
  onConfirm,
  onCancel,
}: {
  pending: PendingAction | null;
  isActing: boolean;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");

  const isApprove = pending?.kind === "approve";
  const name = pending?.displayName || "this user";

  function handleConfirm() {
    onConfirm(note.trim());
    setNote("");
  }

  function handleCancel() {
    setNote("");
    onCancel();
  }

  return (
    <Dialog open={!!pending} onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className={isApprove ? "text-green-700" : "text-red-600"}>
            {isApprove ? (
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                Approve account
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Reject account
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {isApprove
              ? `You are about to approve access for ${name}. They will receive an approval email.`
              : `You are about to reject access for ${name}. They will receive a rejection email.`}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-2">
          <Label htmlFor="action-note" className="text-sm font-medium text-slate-700">
            Note <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <Textarea
            id="action-note"
            placeholder={
              isApprove
                ? "e.g. Verified NMC PIN — welcome to RheumHub"
                : "e.g. Could not verify registration details"
            }
            className="text-sm resize-none h-24"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="text-xs text-slate-400 text-right">{note.length}/500</p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={isActing}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={isActing}
            className={
              isApprove
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"
            }
            onClick={handleConfirm}
          >
            {isActing
              ? isApprove ? "Approving…" : "Rejecting…"
              : isApprove ? "Approve" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [teamFilter, setTeamFilter] = useState("");
  const [sortByTeam, setSortByTeam] = useState(false);
  const [actingUserId, setActingUserId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const { data: users = [], isLoading, error, refetch } = useQuery<AdminUser[]>({
    queryKey: ["admin", "users", statusFilter],
    queryFn: () => {
      const qs = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      return apiFetch(`/api/admin/users${qs}`) as Promise<AdminUser[]>;
    },
  });

  const approveMutation = useMutation({
    mutationFn: ({ userId, note }: { userId: string; note?: string }) =>
      apiFetch(`/api/admin/users/${userId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: note || undefined }),
      }),
    onMutate: ({ userId }) => setActingUserId(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast({ title: "User approved", description: "An approval email has been sent." });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
    onSettled: () => { setActingUserId(null); setPendingAction(null); },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ userId, note }: { userId: string; note?: string }) =>
      apiFetch(`/api/admin/users/${userId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: note || undefined }),
      }),
    onMutate: ({ userId }) => setActingUserId(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast({ title: "User rejected", description: "A rejection email has been sent." });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
    onSettled: () => { setActingUserId(null); setPendingAction(null); },
  });

  function handleConfirm(note: string) {
    if (!pendingAction) return;
    const { userId, kind } = pendingAction;
    if (kind === "approve") {
      approveMutation.mutate({ userId, note: note || undefined });
    } else {
      rejectMutation.mutate({ userId, note: note || undefined });
    }
  }

  // --- Danger zone: purge all users except the calling admin ---
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purgeResult, setPurgeResult] = useState<null | {
    keepUserId: string;
    dbDeleted: Record<string, number>;
    clerkDeleted: number;
    clerkFailed: Array<{ id: string; error: string }>;
  }>(null);

  const purgeMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/admin/users/purge-except-me", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm: "DELETE_ALL_OTHER_USERS" }),
      }) as Promise<{
        keepUserId: string;
        dbDeleted: Record<string, number>;
        clerkDeleted: number;
        clerkFailed: Array<{ id: string; error: string }>;
      }>,
    onSuccess: (data) => {
      setPurgeResult(data);
      setPurgeConfirmText("");
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast({
        title: "Purge complete",
        description: `Removed ${data.clerkDeleted} account${data.clerkDeleted === 1 ? "" : "s"}.`,
      });
    },
    onError: (e: Error) =>
      toast({ title: "Purge failed", description: e.message, variant: "destructive" }),
  });

  const teamFilterTrim = teamFilter.trim().toLowerCase();
  const filteredUsers = teamFilterTrim
    ? users.filter((u) => (u.teamCode ?? "").toLowerCase().startsWith(teamFilterTrim))
    : users;
  const displayedUsers = sortByTeam
    ? [...filteredUsers].sort((a, b) => {
        const at = a.teamCode ?? "";
        const bt = b.teamCode ?? "";
        if (at && !bt) return -1;
        if (!at && bt) return 1;
        return at.localeCompare(bt);
      })
    : filteredUsers;

  const pendingCount = statusFilter === "all"
    ? users.filter((u) => u.approvalStatus === "pending").length
    : statusFilter === "pending" ? users.length : 0;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Users"
        description="Manage pilot access — approve or reject user accounts"
        action={
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 mb-5 text-sm text-amber-800">
          <Clock className="h-4 w-4 shrink-0 text-amber-500" />
          <p>
            <strong>{pendingCount}</strong> account{pendingCount !== 1 ? "s" : ""} awaiting your approval.
          </p>
        </div>
      )}

      <div className="flex items-center gap-3 mb-5">
        <span className="text-xs font-medium text-slate-500">Status</span>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-40 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All users</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs font-medium text-slate-500 ml-2">Team code</span>
        <Input
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          placeholder="e.g. RJ1"
          className="h-8 text-xs font-mono w-32"
          aria-label="Filter by team code"
        />
        {(teamFilter || sortByTeam) && (
          <button
            type="button"
            onClick={() => { setTeamFilter(""); setSortByTeam(false); }}
            className="text-xs text-slate-400 hover:text-slate-700 underline"
          >
            clear
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-slate-400">
          <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Loading users…</p>
        </div>
      ) : error ? (
        <div className="text-center py-16 text-slate-500">
          <Users className="h-8 w-8 mx-auto mb-3 text-amber-400" />
          <p className="font-medium">Failed to load users</p>
          <p className="text-sm mt-1">Please refresh and try again.</p>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="font-medium text-slate-600">
            {statusFilter === "pending" ? "No accounts waiting for approval" : "No users found"}
          </p>
          {statusFilter === "pending" && (
            <p className="text-sm mt-1">New sign-ups will appear here for review.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap">User</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap">Role</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap">Status</th>
                  <th
                    className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap cursor-pointer hover:text-slate-900"
                    onClick={() => setSortByTeam((v) => !v)}
                    title="Click to sort by team code"
                  >
                    Team {sortByTeam ? "▾" : "▴"}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap">Signed up</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.map((user) => (
                  <UserRow
                    key={user.userId}
                    user={user}
                    onApprove={(id, displayName) => setPendingAction({ userId: id, displayName, kind: "approve" })}
                    onReject={(id, displayName) => setPendingAction({ userId: id, displayName, kind: "reject" })}
                    isActing={actingUserId === user.userId}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/50">
            <p className="text-xs text-slate-400">
              {users.length} user{users.length !== 1 ? "s" : ""} shown · Click a row to view onboarding profile
            </p>
          </div>
        </div>
      )}

      <ActionDialog
        pending={pendingAction}
        isActing={actingUserId !== null}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />

      <div className="mt-10 border border-red-200 bg-red-50/50 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900">Danger zone — purge all other users</h3>
            <p className="text-xs text-red-800/80 mt-1 max-w-2xl">
              Permanently deletes every Clerk account and all user-owned data
              (profiles, conversations, portfolios, requests, practices, etc.)
              <strong> except your own admin account</strong>. Audit-log history
              is preserved. There is no undo.
            </p>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white mt-3"
              onClick={() => { setPurgeResult(null); setPurgeConfirmText(""); setPurgeOpen(true); }}
            >
              Purge all other users…
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={purgeOpen} onOpenChange={(o) => { if (!purgeMutation.isPending) setPurgeOpen(o); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Purge all other users
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              This will permanently delete every account except yours, plus all
              of their data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {purgeResult ? (
            <div className="py-2 space-y-3 text-sm">
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-green-900">
                <strong>Done.</strong> Removed {purgeResult.clerkDeleted} Clerk account
                {purgeResult.clerkDeleted === 1 ? "" : "s"}.
                {purgeResult.clerkFailed.length > 0 && (
                  <span className="text-amber-800"> {purgeResult.clerkFailed.length} failed.</span>
                )}
              </div>
              <details className="text-xs text-slate-600">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                  Database rows removed
                </summary>
                <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                  {Object.entries(purgeResult.dbDeleted)
                    .filter(([, n]) => n > 0)
                    .map(([t, n]) => `${t}: ${n}`)
                    .join("\n") || "No data rows needed cleanup."}
                </pre>
              </details>
              <DialogFooter>
                <Button size="sm" onClick={() => setPurgeOpen(false)}>Close</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-2 space-y-3">
              <Label htmlFor="purge-confirm" className="text-sm">
                Type <code className="bg-slate-100 px-1 rounded text-red-700 font-mono">DELETE</code> to enable the button:
              </Label>
              <Input
                id="purge-confirm"
                value={purgeConfirmText}
                onChange={(e) => setPurgeConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                disabled={purgeMutation.isPending}
              />
              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPurgeOpen(false)}
                  disabled={purgeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={purgeConfirmText !== "DELETE" || purgeMutation.isPending}
                  onClick={() => purgeMutation.mutate()}
                >
                  {purgeMutation.isPending ? "Purging…" : "Purge all other users"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
